/* eslint-disable import/no-unresolved */
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-cards';

import { FreeMode, Navigation, Pagination } from 'swiper';
import React, { FC, useEffect, useState } from 'react';
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react';

import type { PaginationOptions } from 'swiper/types';
import { PREDICTION_HORIZON } from '@lib/constants';

type CarouselProps = {
  children: React.ReactNode[];
  id?: string;
  autoHeight?: boolean;
  loop?: boolean;
  slidesPerView?: number;
  bullets?: boolean;
  labeled?: boolean;
  freeMode?: boolean;
  navigation?: boolean;
  progressbar?: boolean;
  onSlideChange?: (swiper: any) => void;
  onInit?: (swiper: any) => void;
  spaceBetween?: number;
  initialSlide?: number;
};

const Carousel: FC<CarouselProps> = ({
  children,
  id,
  autoHeight = true,
  loop = false,
  slidesPerView = 1,
  bullets = false,
  labeled = false,
  freeMode = false,
  navigation = false,
  progressbar = false,
  onSlideChange,
  onInit,
  spaceBetween = undefined,
  initialSlide
}) => {
  const [modules, setModules] = useState([Pagination]);
  const [pagination, setPagination] = useState<PaginationOptions | boolean>(
    false
  );

  const swiper = useSwiper();

  useEffect(() => {
    // adjust height of carousel when used in mobile layout
    if (swiper) {
      swiper.update();
    }
  }, []);

  useEffect(() => {
    if (freeMode) setModules([...modules, FreeMode]);
    if (navigation) setModules([...modules, Navigation]);
    if (progressbar) setPagination({ type: 'progressbar' });
    if (bullets)
      setPagination({
        el: '.custom-pagination',
        clickable: true
      });
    if (labeled) {
      setPagination({
        el: '.custom-pagination-labeled',
        clickable: true,
        dynamicBullets: true,
        dynamicMainBullets: PREDICTION_HORIZON,
        bulletClass: 'labeled-bullet',
        bulletActiveClass: 'labeled-active-bullet',
        // Swiper wants an HTML string. Built by hand so react-dom/server
        // stays out of the client bundle.
        renderBullet: (index, className) =>
          `<div class="${className}"><span class="flex justify-center items-center text-xs font-body font-bold">${
            index + 1
          }</span></div>`
      });
    }
  }, [freeMode, navigation, progressbar, bullets, labeled]);

  return (
    <>
      {bullets && <div className="custom-pagination" />}
      {labeled && <div className="custom-pagination-labeled" />}
      <Swiper
        initialSlide={initialSlide}
        autoHeight={autoHeight}
        id={id}
        freeMode={freeMode}
        loop={loop}
        modules={modules}
        navigation={navigation}
        pagination={pagination}
        observer
        observeParents
        observeSlideChildren
        onClick={(swiper) => {
          setTimeout(() => {
            swiper.updateAutoHeight();
          }, 500);
        }}
        slidesPerView={slidesPerView}
        onSlideChange={onSlideChange}
        onInit={onInit}
        scrollbar={{ draggable: true }}
        allowTouchMove
        simulateTouch
        touchStartPreventDefault={false}
        spaceBetween={spaceBetween}
      >
        {children?.map((item, index) => (
          <SwiperSlide key={`slide_${index}`}>
            <div className="flow-root">{item}</div>
          </SwiperSlide>
        ))}
      </Swiper>
    </>
  );
};

export default Carousel;
